import React from 'react'
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import type { Styles } from '@react-pdf/renderer'

type Style = Styles[string]
import { colors, fonts, type, space, pageGeometry, brand, BRAND_ACCENT } from '../theme'
import { Rule, Spacer, WireLogo } from '../primitives'
import type {
  WireArticleFull,
  WireArticleBlock,
  WireInlineBlock,
  WirePhotoItem,
} from '../../api/types'

// ─── Types ────────────────────────────────────────────────────────────────────

export type ArticleReportProps = {
  article: WireArticleFull
  /** artifactId → pre-fetched data URI (built by useArticlePdf before rendering) */
  imageMap: Record<string, string>
  generatedAt: Date
}

type SourceEntry = { id: string; text: string }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatPublished(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).toUpperCase()
}

function slugToLabel(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

const SECTION_LABELS: Record<string, string> = {
  'summary':             'Key Judgments',
  'scope-note':          'Scope Note',
  'methodology-note':    'Methodology Note',
  'tradecraft-summary':  'Tradecraft Summary',
  'for-additional-info': 'For Additional Information',
  'appendix':            'Appendix',
}

// ─── Portion-mark helper ──────────────────────────────────────────────────────

/** Formats an IC portion-mark string, e.g. "(U) " or "(S//NOFORN) ". Returns '' if falsy. */
function pm(classification: string | undefined, disseminationControls?: string[]): string {
  if (!classification) return ''
  return `(${[classification, ...(disseminationControls ?? [])].join('//')}) `
}

// ─── Table variant system ─────────────────────────────────────────────────────

// Radix violet-4 — used for banded-colored and banded-column-colored variants.
const VIOLET_SUBTLE = '#ebe4ff'

/** PDF hex equivalents of the named TableBg palette (Radix step-3/4 range). */
const TABLE_BG_PDF: Record<string, string> = {
  yellow: '#fefce8',
  blue:   '#dbeafe',
  red:    '#fee2e2',
  green:  '#d1fae5',
  violet: VIOLET_SUBTLE,
  amber:  '#fef3c7',
}

type PdfTableVariant = {
  topBorder:         boolean
  headerBg:          string | undefined
  headerBorderWidth: number
  headerBorderColor: string
  rowBorderWidth:    number
  rowBorderColor:    string
  /** Financial variant: last body row gets a thick bottom border. */
  lastRowThick:      boolean
  rowBg:             (ri: number) => string | undefined
  colBg:             (ci: number) => string | undefined
  tightPad:          boolean
}

function getPdfTableVariant(variant: string | undefined): PdfTableVariant {
  switch (variant) {
    case 'banded':
      return { topBorder: false, headerBg: colors.fill, headerBorderWidth: 1.5, headerBorderColor: colors.lineStrong, rowBorderWidth: 0, rowBorderColor: colors.lineSubtle, lastRowThick: false, rowBg: (ri) => ri % 2 === 1 ? colors.fill : undefined, colBg: () => undefined, tightPad: false }
    case 'banded-colored':
      return { topBorder: false, headerBg: colors.fill, headerBorderWidth: 1.5, headerBorderColor: colors.lineStrong, rowBorderWidth: 0, rowBorderColor: colors.lineSubtle, lastRowThick: false, rowBg: (ri) => ri % 2 === 1 ? VIOLET_SUBTLE : undefined, colBg: () => undefined, tightPad: false }
    case 'financial':
      return { topBorder: true, headerBg: colors.fill, headerBorderWidth: 1.5, headerBorderColor: colors.lineStrong, rowBorderWidth: 0.5, rowBorderColor: colors.lineSubtle, lastRowThick: true, rowBg: () => undefined, colBg: () => undefined, tightPad: true }
    case 'rules':
      return { topBorder: false, headerBg: undefined, headerBorderWidth: 1.5, headerBorderColor: colors.lineStrong, rowBorderWidth: 0.5, rowBorderColor: colors.lineSubtle, lastRowThick: false, rowBg: () => undefined, colBg: () => undefined, tightPad: false }
    case 'banded-column':
      return { topBorder: false, headerBg: colors.fill, headerBorderWidth: 0.75, headerBorderColor: colors.line, rowBorderWidth: 0.5, rowBorderColor: colors.lineSubtle, lastRowThick: false, rowBg: () => undefined, colBg: (ci) => ci % 2 === 1 ? colors.fill : undefined, tightPad: false }
    case 'banded-column-colored':
      return { topBorder: false, headerBg: colors.fill, headerBorderWidth: 0.75, headerBorderColor: colors.line, rowBorderWidth: 0.5, rowBorderColor: colors.lineSubtle, lastRowThick: false, rowBg: () => undefined, colBg: (ci) => ci % 2 === 1 ? VIOLET_SUBTLE : undefined, tightPad: false }
    default: // 'default'
      return { topBorder: false, headerBg: colors.fill, headerBorderWidth: 0.75, headerBorderColor: colors.line, rowBorderWidth: 0.5, rowBorderColor: colors.lineSubtle, lastRowThick: false, rowBg: (ri) => ri % 2 === 1 ? colors.surface : undefined, colBg: () => undefined, tightPad: false }
  }
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  // ── Page ─────────────────────────────────────────────────────────────────
  page: {
    backgroundColor: colors.canvas,
    paddingTop:    pageGeometry.marginTop,
    paddingBottom: pageGeometry.marginBottom,
    paddingLeft:   pageGeometry.marginLeft,
    paddingRight:  pageGeometry.marginRight,
  },

  // ── Header ───────────────────────────────────────────────────────────────
  headerRow: {
    flexDirection: 'row',
    alignItems:    'center',
  },
  brandLockup: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           8,
  },
  brandName: {
    fontFamily:    fonts.serif,
    fontSize:      type.size.xl,
    fontWeight:    type.weight.bold,
    color:         BRAND_ACCENT,
    letterSpacing: 2,
  },
  headerRight: {
    flexGrow:   1,
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontFamily:    fonts.sans,
    fontSize:      type.size.sm,
    fontWeight:    type.weight.bold,
    color:         colors.fgSecondary,
    letterSpacing: 0.5,
  },
  headerDate: {
    fontFamily: fonts.sans,
    fontSize:   type.size.xs,
    color:      colors.fgMuted,
    marginTop:  2,
  },

  // ── Footer ───────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom:    24,
    left:      pageGeometry.marginLeft,
    right:     pageGeometry.marginRight,
  },
  footerRow: {
    flexDirection: 'row',
    marginTop:     4,
  },
  footerText: {
    fontFamily: fonts.sans,
    fontSize:   type.size['2xs'],
    color:      colors.fgMuted,
  },
  footerRight: {
    fontFamily: fonts.sans,
    fontSize:   type.size['2xs'],
    color:      colors.fgMuted,
    flexGrow:   1,
    textAlign:  'right',
  },

  // ── Article header section ────────────────────────────────────────────────
  metaStrip: {
    flexDirection:  'row',
    flexWrap:       'wrap',
    alignItems:     'center',
    gap:            6,
    marginBottom:   space[2],
  },
  metaText: {
    fontFamily: fonts.sans,
    fontSize:   type.size.xs,
    color:      colors.fgMuted,
  },
  articleTitle: {
    fontFamily:  fonts.serif,
    fontSize:    type.size['2xl'],
    fontWeight:  type.weight.bold,
    lineHeight:  type.leading.tight,
    color:       colors.fg,
    marginBottom: space[2],
  },
  articleDescription: {
    fontFamily:  fonts.serif,
    fontSize:    type.size.md,
    fontStyle:   'italic',
    lineHeight:  type.leading.normal,
    color:       colors.fgSecondary,
    marginBottom: space[2],
  },
  articleByline: {
    fontFamily:  fonts.sans,
    fontSize:    type.size.sm,
    color:       colors.fgMuted,
    marginBottom: space[1],
  },

  // ── Thumbnail ─────────────────────────────────────────────────────────────
  thumbnailImage: {
    width:        '100%',
    borderRadius: 2,
    maxHeight:    220,
    objectFit:    'cover',
  },
  photoCaption: {
    fontFamily: fonts.sans,
    fontSize:   type.size.xs,
    color:      colors.fgSecondary,
    marginTop:  3,
  },
  photoCredit: {
    fontFamily: fonts.sans,
    fontSize:   type.size.xs,
    color:      colors.fgMuted,
    fontStyle:  'italic',
  },

  // ── Paragraph ─────────────────────────────────────────────────────────────
  paragraph: {
    fontFamily:   fonts.serif,
    fontSize:     type.size.base,
    lineHeight:   type.leading.relaxed,
    color:        '#000000',
    marginBottom: space[3],
  },

  // ── Headings ──────────────────────────────────────────────────────────────
  h2: {
    fontFamily:   fonts.serif,
    fontSize:     type.size.lg,
    fontWeight:   type.weight.bold,
    lineHeight:   type.leading.snug,
    color:        colors.fg,
    marginTop:    space[5],
    marginBottom: space[2],
  },
  h3: {
    fontFamily:   fonts.serif,
    fontSize:     type.size.md,
    fontWeight:   type.weight.bold,
    lineHeight:   type.leading.snug,
    color:        colors.fg,
    marginTop:    space[4],
    marginBottom: space[1],
  },
  h4: {
    fontFamily:   fonts.serif,
    fontSize:     type.size.base,
    fontWeight:   type.weight.bold,
    lineHeight:   type.leading.snug,
    color:        colors.fg,
    marginTop:    space[3],
    marginBottom: space[1],
  },

  // ── List ──────────────────────────────────────────────────────────────────
  listContainer: {
    marginBottom: space[3],
  },
  listRow: {
    flexDirection: 'row',
    marginBottom:  4,
  },
  listBullet: {
    fontFamily: fonts.serif,
    fontSize:   type.size.base,
    color:      colors.fg,
    width:      12,
    marginTop:  1,
  },
  listItemText: {
    fontFamily: fonts.serif,
    fontSize:   type.size.base,
    lineHeight: type.leading.relaxed,
    color:      colors.fg,
    flex:       1,
  },

  // ── Section marker ────────────────────────────────────────────────────────
  sectionMarkerContainer: {
    borderTopWidth:  2,
    borderTopColor:  BRAND_ACCENT,
    paddingTop:      space[3],
    marginTop:       space[6],
    marginBottom:    space[3],
  },
  sectionMarkerLabel: {
    fontFamily:    fonts.sans,
    fontSize:      type.size.xs,
    fontWeight:    type.weight.bold,
    color:         BRAND_ACCENT,
    letterSpacing: 1,
  },

  // ── Warning ───────────────────────────────────────────────────────────────
  warningContainer: {
    backgroundColor: colors.warningFill,
    borderWidth:     0.75,
    borderColor:     colors.warningLine,
    borderRadius:    3,
    paddingHorizontal: space[3],
    paddingVertical:   space[2],
    marginBottom:    space[4],
  },
  warningText: {
    fontFamily:  fonts.sans,
    fontSize:    type.size.sm,
    fontWeight:  type.weight.bold,
    color:       colors.warningFg,
    lineHeight:  type.leading.normal,
  },

  // ── Textbox ───────────────────────────────────────────────────────────────
  textboxContainer: {
    backgroundColor: colors.fill,
    borderWidth:     0.75,
    borderColor:     colors.line,
    borderRadius:    3,
    paddingHorizontal: space[3],
    paddingVertical:   space[3],
    marginBottom:    space[4],
  },
  textboxTitle: {
    fontFamily:    fonts.sans,
    fontSize:      type.size['2xs'],
    fontWeight:    type.weight.bold,
    color:         colors.fgSecondary,
    letterSpacing: 1.5,
    marginBottom:  space[2],
  },

  // ── Source list ───────────────────────────────────────────────────────────
  sourceListContainer: {
    borderTopWidth: 0.75,
    borderTopColor: colors.line,
    paddingTop:     space[4],
    marginTop:      space[2],
    marginBottom:   space[4],
  },
  sourceListLabel: {
    fontFamily:    fonts.sans,
    fontSize:      type.size.xs,
    fontWeight:    type.weight.bold,
    color:         colors.fgSecondary,
    letterSpacing: 1,
    marginBottom:  space[2],
  },
  sourceRow: {
    flexDirection: 'row',
    marginBottom:  space[1],
  },
  sourceNumber: {
    fontFamily: fonts.sans,
    fontSize:   type.size.sm,
    color:      colors.fgMuted,
    width:      18,
    marginTop:  1,
  },
  sourceText: {
    fontFamily: fonts.sans,
    fontSize:   type.size.sm,
    color:      colors.fgSecondary,
    lineHeight: type.leading.normal,
    flex:       1,
  },

  // ── Table ─────────────────────────────────────────────────────────────────
  tableContainer: {
    marginBottom: space[4],
  },
  tableCaption: {
    fontFamily:     fonts.sans,
    fontSize:       type.size.xs,
    fontStyle:      'italic',
    color:          colors.fgSecondary,
    borderBottomWidth: 0.75,
    borderBottomColor: colors.line,
    paddingBottom:  4,
    marginBottom:   3,
  },
  tableHeaderRow: {
    flexDirection: 'row',
  },
  tableBodyRow: {
    flexDirection: 'row',
  },
  tableHeaderCell: {
    flex:              1,
    paddingHorizontal: 6,
    paddingVertical:   4,
    fontFamily:        fonts.sans,
    fontSize:          type.size['2xs'],
    fontWeight:        type.weight.bold,
    color:             colors.fgSecondary,
    letterSpacing:     0.8,
  },
  tableBodyCell: {
    flex:              1,
    paddingHorizontal: 6,
    paddingVertical:   4,
    fontFamily:        fonts.sans,
    fontSize:          type.size.sm,
    color:             colors.fg,
    lineHeight:        type.leading.normal,
  },
  tableBodyCellBold: {
    fontWeight: type.weight.bold,
  },

  // ── Photo (single) ────────────────────────────────────────────────────────
  photoContainer: {
    marginBottom: space[4],
  },
  photoImage: {
    width:        '100%',
    borderRadius: 2,
  },
  photoCaptionRow: {
    marginTop: 4,
  },

  // ── Photo group ───────────────────────────────────────────────────────────
  photoGroupContainer: {
    marginBottom: space[4],
  },
  photoGroupTitle: {
    fontFamily:   fonts.sans,
    fontSize:     type.size.xs,
    fontWeight:   type.weight.bold,
    color:        colors.fgSecondary,
    letterSpacing: 0.8,
    marginBottom: space[2],
  },
  photoGroupGrid: {
    flexDirection: 'row',
    flexWrap:      'wrap',
    gap:           4,
  },

  // ── Photo comparison ──────────────────────────────────────────────────────
  photoComparisonContainer: {
    marginBottom: space[4],
  },
  photoComparisonRow: {
    flexDirection: 'row',
    gap:           4,
  },
  photoComparisonSide: {
    flex: 1,
  },
  photoComparisonLabel: {
    fontFamily:    fonts.sans,
    fontSize:      type.size.xs,
    fontWeight:    type.weight.bold,
    color:         colors.fgMuted,
    letterSpacing: 0.8,
    marginTop:     3,
    textAlign:     'center',
  },

  // ── Visual placeholder ────────────────────────────────────────────────────
  visualPlaceholder: {
    backgroundColor: colors.fill,
    borderWidth:     0.75,
    borderColor:     colors.lineSubtle,
    borderRadius:    3,
    paddingVertical: space[5],
    alignItems:      'center',
    marginBottom:    space[4],
  },
  visualPlaceholderText: {
    fontFamily: fonts.sans,
    fontSize:   type.size.sm,
    color:      colors.fgMuted,
    textAlign:  'center',
  },
  visualPlaceholderSub: {
    fontFamily: fonts.sans,
    fontSize:   type.size.xs,
    color:      colors.fgMuted,
    textAlign:  'center',
    marginTop:  3,
  },

  // ── Portion mark (IC inline marker) ──────────────────────────────────────
  portionMark: {
    fontFamily: fonts.sans,
    fontSize:   type.size['2xs'],
    color:      '#3d3d3d',
  },
})

// ─── Inline renderer ──────────────────────────────────────────────────────────

/**
 * Renders inline content (spans, links, citations) as nested react-pdf Text nodes.
 * Must always be used inside a parent <Text> element.
 */
function renderInlineNodes(
  nodes: WireInlineBlock[],
  sources: SourceEntry[],
): React.ReactNode[] {
  return nodes.map((node, i) => {
    if (node.type === 'span') {
      const marks = new Set(node.typography ?? [])
      const isCode = marks.has('text-code')
      return (
        <Text
          key={i}
          style={{
            fontFamily:  isCode ? fonts.mono : fonts.serif,
            fontWeight:  marks.has('text-bold') ? type.weight.bold : type.weight.normal,
            fontStyle:   marks.has('text-italic') ? 'italic' : 'normal',
            fontSize:    (marks.has('text-superscript') || marks.has('text-subscript'))
                           ? type.size.xs
                           : undefined,
          }}
        >
          {node.text}
        </Text>
      )
    }

    if (node.type === 'link') {
      return (
        <Text key={i} style={{ fontFamily: fonts.serif, color: colors.accentText }}>
          {node.text}
        </Text>
      )
    }

    if (node.type === 'citation') {
      const idx = sources.findIndex(s => s.id === node.sourceId)
      return (
        <Text key={i} style={{ fontFamily: fonts.sans, fontSize: type.size.xs, color: colors.fgSecondary }}>
          {`[${idx >= 0 ? idx + 1 : '?'}]`}
        </Text>
      )
    }

    return null
  })
}

// ─── Block renderers ──────────────────────────────────────────────────────────

type RenderBlock = (block: WireArticleBlock) => React.ReactNode

function ParagraphPdf({
  block,
  sources,
}: {
  block: Extract<WireArticleBlock, { type: 'paragraph' }>
  sources: SourceEntry[]
}) {
  const portionMark = pm(block.classification, block.disseminationControls)
  return (
    <Text style={s.paragraph}>
      {portionMark && <Text style={s.portionMark}>{portionMark}</Text>}
      {renderInlineNodes(block.children, sources)}
    </Text>
  )
}

function HeadingPdf({
  block,
}: {
  block: Extract<WireArticleBlock, { type: 'heading' }>
}) {
  const style = block.level <= 2 ? s.h2 : block.level === 3 ? s.h3 : s.h4
  const portionMark = pm(block.classification, block.disseminationControls)
  return (
    <Text style={style}>
      {portionMark && <Text style={s.portionMark}>{portionMark}</Text>}
      {block.content}
    </Text>
  )
}

function ListPdf({
  block,
  sources,
}: {
  block: Extract<WireArticleBlock, { type: 'list' }>
  sources: SourceEntry[]
}) {
  const portionMark = pm(block.classification, block.disseminationControls)
  return (
    <View>
      {portionMark && <Text style={s.portionMark}>{portionMark}</Text>}
      <View style={s.listContainer}>
        {block.items.map((item, i) => (
          <View key={item.id} style={s.listRow}>
            <Text style={s.listBullet}>
              {block.style === 'ordered' ? `${i + 1}.` : '•'}
            </Text>
            <Text style={s.listItemText}>
              {renderInlineNodes(item.children, sources)}
            </Text>
          </View>
        ))}
      </View>
    </View>
  )
}

function SectionMarkerPdf({
  block,
}: {
  block: Extract<WireArticleBlock, { type: 'section-marker' }>
}) {
  const label = SECTION_LABELS[block.sectionType]
    ?? block.sectionType.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  const portionMark = pm(block.classification, block.disseminationControls)
  return (
    <View style={s.sectionMarkerContainer}>
      <Text style={s.sectionMarkerLabel}>
        {portionMark && <Text style={[s.portionMark, { color: BRAND_ACCENT }]}>{portionMark}</Text>}
        {label.toUpperCase()}
      </Text>
    </View>
  )
}

function WarningPdf({
  block,
}: {
  block: Extract<WireArticleBlock, { type: 'warning' }>
}) {
  return (
    <View style={s.warningContainer}>
      <Text style={s.warningText}>{block.content}</Text>
    </View>
  )
}

function TextboxPdf({
  block,
  renderBlock,
}: {
  block: Extract<WireArticleBlock, { type: 'textbox' }>
  renderBlock: RenderBlock
}) {
  return (
    <View style={s.textboxContainer}>
      {block.title && (
        <Text style={s.textboxTitle}>{block.title.toUpperCase()}</Text>
      )}
      {block.children.map(child => (
        <View key={child.id}>{renderBlock(child)}</View>
      ))}
    </View>
  )
}

function SourceListPdf({
  block,
}: {
  block: Extract<WireArticleBlock, { type: 'source-list' }>
}) {
  if (!block.entries.length) return null
  return (
    <View style={s.sourceListContainer}>
      <Text style={s.sourceListLabel}>SOURCES</Text>
      {block.entries.map((entry, i) => (
        <View key={entry.id} style={s.sourceRow}>
          <Text style={s.sourceNumber}>{i + 1}.</Text>
          <Text style={s.sourceText}>{entry.text}</Text>
        </View>
      ))}
    </View>
  )
}

function TablePdf({
  block,
  sources,
}: {
  block: Extract<WireArticleBlock, { type: 'table' }>
  sources: SourceEntry[]
}) {
  const headerRows  = block.rows.filter(r => r.rowType === 'header')
  const bodyRows    = block.rows.filter(r => r.rowType === 'body')
  const portionMark = pm(block.classification, block.disseminationControls)
  const v           = getPdfTableVariant(block.variant)

  // ── Column widths ─────────────────────────────────────────────────────────
  // Infer column count from the first row, then resolve each column's flex
  // weight from columnStyles.widthPct, distributing any unassigned width evenly.
  const numCols = block.rows[0]?.cells.reduce((n, c) => n + (c.colSpan ?? 1), 0) ?? 1
  const rawWidths = Array.from({ length: numCols }, (_, ci) =>
    block.columnStyles[ci]?.widthPct ?? null
  )
  const totalAssigned = rawWidths.reduce<number>((sum, w) => sum + (w ?? 0), 0)
  const missingCount  = rawWidths.filter(w => w === null).length
  const defaultW      = missingCount > 0 ? (100 - totalAssigned) / missingCount : 100 / numCols
  const colWidths     = rawWidths.map(w => w ?? defaultW)

  function colFlex(colPos: number, span: number): number {
    return colWidths.slice(colPos, colPos + span).reduce((s, w) => s + w, 0)
  }

  const pad = v.tightPad
    ? { paddingHorizontal: 5, paddingVertical: 2 }
    : { paddingHorizontal: 6, paddingVertical: 4 }

  return (
    <View style={[s.tableContainer, v.topBorder ? { borderTopWidth: 1.5, borderTopColor: colors.lineStrong } : {}]}>
      {portionMark && <Text style={s.portionMark}>{portionMark}</Text>}
      {block.caption && <Text style={s.tableCaption}>{block.caption}</Text>}

      {/* ── Header rows ── */}
      {headerRows.map((row, ri) => {
        let colPos = 0
        return (
          <View
            key={ri}
            style={[
              s.tableHeaderRow,
              v.headerBg ? { backgroundColor: v.headerBg } : {},
              { borderBottomWidth: v.headerBorderWidth, borderBottomColor: v.headerBorderColor },
            ]}
          >
            {row.cells.map((cell, ci) => {
              const span   = cell.colSpan ?? 1
              const flex   = colFlex(colPos, span)
              const colBg  = v.colBg(colPos)
              const align  = cell.align ?? block.columnStyles[colPos]?.align ?? 'left'
              colPos += span
              return (
                <Text
                  key={ci}
                  style={[
                    s.tableHeaderCell,
                    pad,
                    { flex, textAlign: align },
                    colBg ? { backgroundColor: colBg } : {},
                  ]}
                >
                  {renderInlineNodes(cell.children, sources)}
                </Text>
              )
            })}
          </View>
        )
      })}

      {/* ── Body rows ── */}
      {bodyRows.map((row, ri) => {
        const isLast       = ri === bodyRows.length - 1
        const variantRowBg = v.rowBg(ri)
        const rowBg = row.bg
          ? TABLE_BG_PDF[row.bg]
          : row.highlighted
            ? colors.fill
            : variantRowBg
        // Explicit row overrides (editor-set row.bg or highlighted) suppress column colors.
        // Variant row stripes do NOT suppress explicit column overrides — column wins.
        const hasExplicitRowBg = Boolean(row.bg || row.highlighted)

        let colPos = 0
        return (
          <View
            key={ri}
            style={[
              s.tableBodyRow,
              rowBg ? { backgroundColor: rowBg } : {},
              v.rowBorderWidth > 0
                ? {
                    borderBottomWidth:  v.lastRowThick && isLast ? 1.5 : v.rowBorderWidth,
                    borderBottomColor:  v.lastRowThick && isLast ? colors.lineStrong : v.rowBorderColor,
                  }
                : {},
            ]}
          >
            {row.cells.map((cell, ci) => {
              const span          = cell.colSpan ?? 1
              const flex          = colFlex(colPos, span)
              const isRowHeader   = ci === 0 && block.hasHeaderColumn
              const colBgOverride = block.columnStyles[colPos]?.bg
              // Explicit column override always wins unless an explicit row override is active.
              // Variant column bg is suppressed when any row bg (explicit or variant) is present.
              const colBg = hasExplicitRowBg
                ? undefined
                : colBgOverride
                  ? TABLE_BG_PDF[colBgOverride]
                  : (variantRowBg ? undefined : v.colBg(colPos))
              const align = cell.align ?? block.columnStyles[colPos]?.align ?? 'left'
              colPos += span
              return (
                <Text
                  key={ci}
                  style={[
                    isRowHeader ? s.tableHeaderCell : s.tableBodyCell,
                    pad,
                    { flex, textAlign: align },
                    colBg ? { backgroundColor: colBg } : {},
                    (row.bold || isRowHeader) ? s.tableBodyCellBold : {},
                  ]}
                >
                  {renderInlineNodes(cell.children, sources)}
                </Text>
              )
            })}
          </View>
        )
      })}
    </View>
  )
}

// ── Photo caption helper ───────────────────────────────────────────────────────

function PhotoCaptionRow({
  imageClassification,
  imageDisseminationControls,
  caption,
  captionClassification,
  captionDisseminationControls,
  credit,
}: {
  imageClassification?: string
  imageDisseminationControls?: string[]
  caption?: string
  captionClassification?: string
  captionDisseminationControls?: string[]
  credit?: string
}) {
  const imgPm = pm(imageClassification, imageDisseminationControls)
  const capPm = pm(captionClassification, captionDisseminationControls)
  if (!imgPm && !caption && !credit) return null
  return (
    <View style={s.photoCaptionRow}>
      <Text style={s.photoCaption}>
        {imgPm ? <Text style={s.portionMark}>{imgPm}</Text> : null}
        {caption !== undefined && imgPm ? '· ' : null}
        {caption !== undefined && capPm ? <Text style={s.portionMark}>{capPm}</Text> : null}
        {caption}
      </Text>
      {credit && <Text style={s.photoCredit}>{credit}</Text>}
    </View>
  )
}

// ── Single photo item ──────────────────────────────────────────────────────────

function PhotoItemImg({
  photo,
  imageMap,
  style,
}: {
  photo: WirePhotoItem
  imageMap: Record<string, string>
  style?: Style
}) {
  const src = imageMap[photo.artifactId] ?? ''
  if (!src) return null
  return <Image src={src} style={style ? [s.photoImage, style] : s.photoImage} />
}

function PhotoBlockPdf({
  block,
  imageMap,
}: {
  block: Extract<WireArticleBlock, { type: 'photo' }>
  imageMap: Record<string, string>
}) {
  return (
    <View wrap={false} style={s.photoContainer}>
      <PhotoItemImg photo={block.photo} imageMap={imageMap} />
      <PhotoCaptionRow
        imageClassification={block.photo.classification}
        imageDisseminationControls={block.photo.disseminationControls}
        caption={block.photo.caption}
        captionClassification={block.photo.captionClassification}
        captionDisseminationControls={block.photo.captionDisseminationControls}
        credit={block.photo.credit}
      />
    </View>
  )
}

function PhotoGroupPdf({
  block,
  imageMap,
}: {
  block: Extract<WireArticleBlock, { type: 'photo-group' }>
  imageMap: Record<string, string>
}) {
  const cols = block.display.type === 'grid' ? (block.display.columns ?? 2) : 2
  const widthPct = `${Math.floor((100 - (cols - 1)) / cols)}%` as `${number}%`
  const groupTitlePm = pm(block.groupTitleClassification, block.groupTitleDisseminationControls)

  return (
    <View wrap={false} style={s.photoGroupContainer}>
      {block.groupTitle && (
        <Text style={s.photoGroupTitle}>
          {groupTitlePm && <Text style={s.portionMark}>{groupTitlePm}</Text>}
          {block.groupTitle.toUpperCase()}
        </Text>
      )}
      <View style={s.photoGroupGrid}>
        {block.photos.map(photo => {
          const src = imageMap[photo.artifactId] ?? ''
          if (!src) return null
          return (
            <View key={photo.id} style={{ width: widthPct }}>
              <Image src={src} style={s.photoImage} />
              <PhotoCaptionRow
                imageClassification={photo.classification}
                imageDisseminationControls={photo.disseminationControls}
                caption={photo.caption}
                captionClassification={photo.captionClassification}
                captionDisseminationControls={photo.captionDisseminationControls}
              />
            </View>
          )
        })}
      </View>
    </View>
  )
}

function PhotoComparisonPdf({
  block,
  imageMap,
}: {
  block: Extract<WireArticleBlock, { type: 'photo-comparison' }>
  imageMap: Record<string, string>
}) {
  const beforeSrc = imageMap[block.before.artifactId] ?? ''
  const afterSrc  = imageMap[block.after.artifactId] ?? ''
  const capPm     = pm(block.captionClassification, block.captionDisseminationControls)

  return (
    <View wrap={false} style={s.photoComparisonContainer}>
      <View style={s.photoComparisonRow}>
        {beforeSrc && (
          <View style={s.photoComparisonSide}>
            <Image src={beforeSrc} style={s.photoImage} />
            <Text style={s.photoComparisonLabel}>
              {pm(block.before.classification, block.before.disseminationControls)}
              {block.beforeLabel ?? 'BEFORE'}
            </Text>
          </View>
        )}
        {afterSrc && (
          <View style={s.photoComparisonSide}>
            <Image src={afterSrc} style={s.photoImage} />
            <Text style={s.photoComparisonLabel}>
              {pm(block.after.classification, block.after.disseminationControls)}
              {block.afterLabel ?? 'AFTER'}
            </Text>
          </View>
        )}
      </View>
      {block.caption && (
        <Text style={[s.photoCaption, { marginTop: space[1] }]}>
          {capPm ? <Text style={s.portionMark}>{capPm}</Text> : null}
          {block.caption}
        </Text>
      )}
    </View>
  )
}

function VisualPlaceholderPdf({
  block,
  imageMap,
}: {
  block: Extract<WireArticleBlock, { type: 'visual' }>
  imageMap: Record<string, string>
}) {
  const visualPm = pm(
    block.visualClassification ?? block.classification,
    block.visualDisseminationControls ?? block.disseminationControls,
  )
  const thumbnailSrc = imageMap[block.artifactId] ?? ''
  if (thumbnailSrc) {
    return (
      <View style={s.visualPlaceholder}>
        {visualPm && <Text style={s.portionMark}>{visualPm}</Text>}
        <Image src={thumbnailSrc} style={{ width: '100%', objectFit: 'contain' }} />
      </View>
    )
  }
  return (
    <View style={s.visualPlaceholder}>
      {visualPm && <Text style={s.portionMark}>{visualPm}</Text>}
      <Text style={s.visualPlaceholderText}>
        {block.title ?? (block.jobNumber ? `Visual ${block.jobNumber}` : 'Interactive Visual')}
      </Text>
      <Text style={s.visualPlaceholderSub}>
        This visual is not available in the PDF export.
      </Text>
    </View>
  )
}

function VideoPlaceholderPdf({
  block,
  imageMap,
}: {
  block: Extract<WireArticleBlock, { type: 'video' }>
  imageMap: Record<string, string>
}) {
  const posterSrc = block.posterArtifactId ? (imageMap[block.posterArtifactId] ?? '') : ''
  const capPm = pm(block.captionClassification, block.captionDisseminationControls)

  return (
    <View wrap={false} style={s.photoContainer}>
      {posterSrc ? (
        <Image src={posterSrc} style={s.photoImage} />
      ) : (
        <View style={s.visualPlaceholder}>
          <Text style={s.visualPlaceholderText}>
            {block.altText ?? 'Video'}
          </Text>
          <Text style={s.visualPlaceholderSub}>
            This video is not available in the PDF export.
          </Text>
        </View>
      )}
      {(block.caption || block.credit) && (
        <View style={s.photoCaptionRow}>
          {block.caption && (
            <Text style={s.photoCaption}>
              {capPm ? <Text style={s.portionMark}>{capPm}</Text> : null}
              {block.caption}
            </Text>
          )}
          {block.credit && <Text style={s.photoCredit}>{block.credit}</Text>}
        </View>
      )}
    </View>
  )
}

function AudioPlaceholderPdf({
  block,
}: {
  block: Extract<WireArticleBlock, { type: 'audio' }>
}) {
  const capPm = pm(block.captionClassification, block.captionDisseminationControls)

  return (
    <View wrap={false} style={s.photoContainer}>
      <View style={s.visualPlaceholder}>
        <Text style={s.visualPlaceholderText}>
          {block.title ?? block.altText ?? 'Audio'}
        </Text>
        <Text style={s.visualPlaceholderSub}>
          This audio is not available in the PDF export.
        </Text>
      </View>
      {(block.caption || block.credit) && (
        <View style={s.photoCaptionRow}>
          {block.caption && (
            <Text style={s.photoCaption}>
              {capPm ? <Text style={s.portionMark}>{capPm}</Text> : null}
              {block.caption}
            </Text>
          )}
          {block.credit && <Text style={s.photoCredit}>{block.credit}</Text>}
        </View>
      )}
    </View>
  )
}

// ─── Document ─────────────────────────────────────────────────────────────────

export function ArticleReport({ article, imageMap, generatedAt }: ArticleReportProps) {

  // Derive the source list for citation resolution
  const sources: SourceEntry[] = (() => {
    const block = article.blocks.find(
      (b): b is Extract<WireArticleBlock, { type: 'source-list' }> => b.type === 'source-list',
    )
    return block?.entries ?? []
  })()

  // ── Block renderer ────────────────────────────────────────────────────────
  function renderBlock(block: WireArticleBlock): React.ReactNode {
    switch (block.type) {
      case 'paragraph':
        return <ParagraphPdf key={block.id} block={block} sources={sources} />
      case 'heading':
        return <HeadingPdf key={block.id} block={block} />
      case 'list':
        return <ListPdf key={block.id} block={block} sources={sources} />
      case 'section-marker':
        return <SectionMarkerPdf key={block.id} block={block} />
      case 'warning':
        return <WarningPdf key={block.id} block={block} />
      case 'textbox':
        return (
          <TextboxPdf
            key={block.id}
            block={block}
            renderBlock={renderBlock}
          />
        )
      case 'source-list':
        return <SourceListPdf key={block.id} block={block} />
      case 'table':
        return <TablePdf key={block.id} block={block} sources={sources} />
      case 'photo':
        return <PhotoBlockPdf key={block.id} block={block} imageMap={imageMap} />
      case 'photo-group':
        return <PhotoGroupPdf key={block.id} block={block} imageMap={imageMap} />
      case 'photo-comparison':
        return <PhotoComparisonPdf key={block.id} block={block} imageMap={imageMap} />
      case 'video':
        return <VideoPlaceholderPdf key={block.id} block={block} imageMap={imageMap} />
      case 'audio':
        return <AudioPlaceholderPdf key={block.id} block={block} />
      case 'visual':
        return <VisualPlaceholderPdf key={block.id} block={block} imageMap={imageMap} />
      // classification-banner, report-details, attribution → suppressed (same as web renderer)
      default:
        return null
    }
  }

  // ── Thumbnail ─────────────────────────────────────────────────────────────
  const thumbnailSrc = article.thumbnail
    ? (imageMap[article.thumbnail.artifactId ?? '__thumbnail__'] ?? '')
    : ''

  // ── Metadata strip ────────────────────────────────────────────────────────
  const metaParts: string[] = []
  if (article.regions?.length)  metaParts.push(...article.regions.map(slugToLabel))
  if (article.topics?.length)   metaParts.push(...article.topics.map(slugToLabel))
  if (article.publishedAt)      metaParts.push(formatPublished(article.publishedAt))
  if (article.readTime != null) metaParts.push(`${article.readTime} min read`)

  return (
    <Document
      title={article.title}
      author={brand.name}
      subject={article.description ?? ''}
      creator={`${brand.name} ${brand.tagline}`}
    >
      <Page size={pageGeometry.size} style={s.page}>

        {/* ── Fixed footer — repeated on every page ── */}
        <View style={s.footer} fixed>
          <Rule />
          <View style={s.footerRow}>
            <Text style={s.footerText}>
              {`${brand.name} ${brand.tagline}  ·  ${article.classification}  ·  ${formatDate(generatedAt)}`}
            </Text>
            <Text
              style={s.footerRight}
              render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            />
          </View>
        </View>

        {/* ── Page header ── */}
        <View style={s.headerRow}>
          <View style={s.brandLockup}>
            <WireLogo height={22} />
            <Text style={s.brandName}>{brand.name}</Text>
          </View>
          <View style={s.headerRight}>
            <Text style={s.headerTitle}>Intelligence Briefing</Text>
            <Text style={s.headerDate}>{formatDate(generatedAt)}</Text>
          </View>
        </View>
        <Spacer size={2} />
        <Rule accent />

        <Spacer size={6} />

        {/* ── Article header section ── */}
        {metaParts.length > 0 && (
          <View style={s.metaStrip}>
            {metaParts.map((part, i) => (
              <React.Fragment key={i}>
                <Text style={s.metaText}>{part}</Text>
                {i < metaParts.length - 1 && (
                  <Text style={[s.metaText, { color: colors.lineStrong }]}>·</Text>
                )}
              </React.Fragment>
            ))}
          </View>
        )}

        <Text style={s.articleTitle}>
          {pm(article.classification, article.disseminationControls)}
          {article.title}
        </Text>

        {article.description && (
          <Text style={s.articleDescription}>
            {pm(article.classification, article.disseminationControls)}
            {article.description}
          </Text>
        )}

        {article.byline && (
          <Text style={s.articleByline}>{article.byline}</Text>
        )}

        <Spacer size={4} />
        <Rule subtle />
        <Spacer size={4} />

        {/* ── Thumbnail ── */}
        {thumbnailSrc && (
          <View wrap={false} style={s.photoContainer}>
            <Image src={thumbnailSrc} style={s.thumbnailImage} />
            <PhotoCaptionRow
              imageClassification={article.thumbnail?.classification}
              imageDisseminationControls={article.thumbnail?.disseminationControls}
              caption={article.thumbnail?.caption}
              captionClassification={article.thumbnail?.captionClassification}
              captionDisseminationControls={article.thumbnail?.captionDisseminationControls}
              credit={article.thumbnail?.credit}
            />
          </View>
        )}

        {/* ── Body blocks ── */}
        {article.blocks.map(block => renderBlock(block))}

      </Page>
    </Document>
  )
}
